import * as React from 'react'
import dynamic from 'next/dynamic'

import { i18n } from '@thg-commerce/enterprise-core'

import {
  CodeTitle,
  Container,
  InputWrapper,
  MainSubtitle,
  MainTitle,
  ReferralContainer,
  ReferralsItem,
  ReferralsItemsContainer,
  ReferralsItemTitle,
  ReferralsItemValue,
  ReferralsTermsText,
  ReferralsTitle,
  SecondarySubtitle,
  ShareTitle,
  StyledCode,
  StyledCopyToClipboardIcon,
  StyledInput,
  StyledReferralIconLi,
  StyledReferralIconLink,
  StyledSeparator,
  StyledSeparatorText,
  StyledWrapper,
} from './styles'
import { DynamicReferralEmailModal } from './DynamicReferralEmailModal'

const CopyToClipboardIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/CopyToClipboard'),
)

const CircularFacebook = dynamic(
  () =>
    import(
      '@thg-commerce/gravity-icons/src/components/SocialIcons/Colour/CircularFacebook'
    ),
)

const CircularInstagram = dynamic(
  () =>
    import(
      '@thg-commerce/gravity-icons/src/components/SocialIcons/Colour/CircularInstagram'
    ),
)

const CircularTwitter = dynamic(
  () =>
    import(
      '@thg-commerce/gravity-icons/src/components/SocialIcons/Colour/CircularTwitter'
    ),
)

const CircularWhatsapp = dynamic(
  () =>
    import(
      '@thg-commerce/gravity-icons/src/components/SocialIcons/Colour/CircularWhatsapp'
    ),
)

const CircularEmail = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/CircularEmail'),
)

const CircularSms = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/CircularSms'),
)

const CircularMessenger = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/CircularMessenger'),
)

export interface DynamicReferralProps {
  customerFullName?: string
  customerReferralCount?: number
  customerReferralCode?: string
  customerSocialReferralMethods?: {
    type: string
    url?: string
  }[]
  customerReferralLink?: string
  customerCreditBalance?: string
  mainTitle?: string
  mainSubtitle?: string
  balanceNote?: string
  showYourCodeSection?: string
  codeSubtitle?: string
}

const Separator = ({ middleText }: { middleText: string }) => (
  <StyledSeparator>
    <StyledSeparatorText>{middleText}</StyledSeparatorText>
  </StyledSeparator>
)

const getReferralIcons = (name: string) => {
  switch (name) {
    case 'EMAIL':
      return <CircularEmail />
    case 'SMS':
      return <CircularSms />
    case 'WHATSAPP':
      return <CircularWhatsapp />
    case 'TWITTER':
      return <CircularTwitter />
    case 'MESSENGER':
      return <CircularMessenger />
    case 'FACEBOOK':
      return <CircularFacebook />
    case 'INSTAGRAM':
      return <CircularInstagram />
    default:
      return null
  }
}

export const DynamicReferral = (props: DynamicReferralProps) => {
  const [openModal, setOpenModal] = React.useState(false)

  const i18nText = {
    referralsTitle: i18n('header.account.your.referrals.text'),
    balanceTitle: i18n('account.referrals.balance.title'),
    countTitle: i18n('referrals.count.explaination'),
    shareTitle: i18n('account.referrals.share.title'),
    orText: i18n('general.or.text'),
  }

  const mainTitle = props.customerFullName
    ? props.mainTitle?.replace('%s', props.customerFullName)
    : props.mainTitle?.replace(' %s', '')

  const mainSubtitleText = props.mainSubtitle?.split('<br><br>') || ''

  return (
    <Container>
      {mainTitle && <MainTitle data-testid="main-title">{mainTitle}</MainTitle>}
      {props.mainSubtitle && (
        <MainSubtitle
          data-testid="main-subtitle"
          content={mainSubtitleText?.[0]}
        />
      )}
      {props.mainSubtitle && mainSubtitleText?.[1] && (
        <SecondarySubtitle
          data-testid="secondary-subtitle"
          content={mainSubtitleText?.[1]}
        />
      )}
      <StyledWrapper>
        <ReferralContainer>
          <ReferralsTitle>{i18nText.referralsTitle}</ReferralsTitle>
          <ReferralsItemsContainer>
            <ReferralsItem>
              <ReferralsItemTitle>{i18nText.balanceTitle}</ReferralsItemTitle>
              <ReferralsItemValue>
                {props.customerCreditBalance}
              </ReferralsItemValue>
            </ReferralsItem>
            <ReferralsItem>
              <ReferralsItemTitle>{i18nText.countTitle}</ReferralsItemTitle>
              <ReferralsItemValue>
                {props.customerReferralCount}
              </ReferralsItemValue>
            </ReferralsItem>
          </ReferralsItemsContainer>
          {props.balanceNote && (
            <ReferralsTermsText data-testid="balance-note">
              {props.balanceNote}
            </ReferralsTermsText>
          )}
        </ReferralContainer>
        <ShareTitle>{i18nText.shareTitle}</ShareTitle>
        {props.customerReferralLink && (
          <React.Fragment>
            <InputWrapper data-testid="customer-referral-link">
              <StyledInput
                type="text"
                readOnly
                placeholder={props.customerReferralLink}
              />
              <StyledCopyToClipboardIcon
                data-testid="copy-referral-link"
                ariaLabel={i18nText.shareTitle}
                sizing="micro"
                onClick={async () => {
                  props.customerReferralLink &&
                    (await window?.navigator?.clipboard?.writeText(
                      props.customerReferralLink,
                    ))
                }}
              >
                <CopyToClipboardIcon />
              </StyledCopyToClipboardIcon>
            </InputWrapper>
            <Separator middleText={i18nText.orText} />
          </React.Fragment>
        )}
        {props.customerSocialReferralMethods &&
          props.customerSocialReferralMethods.length > 0 && (
            <React.Fragment>
              <ul>
                {props.customerSocialReferralMethods.map((item, index) => {
                  return (
                    <StyledReferralIconLi key={`social-referral-${index}`}>
                      <StyledReferralIconLink
                        href={item.url || '#'}
                        aria-label={item.type}
                        onClick={(e) => {
                          e.preventDefault()
                          if (item.type === 'EMAIL') {
                            setOpenModal(true)
                          } else {
                            window.open(
                              item.url,
                              '_blank',
                              'width=600, height=400',
                            )
                          }
                        }}
                        data-testid={`social-referral-${item.type}`}
                      >
                        {getReferralIcons(item.type)}
                      </StyledReferralIconLink>
                    </StyledReferralIconLi>
                  )
                })}
              </ul>
            </React.Fragment>
          )}

        {props.codeSubtitle &&
          props.customerReferralCode &&
          props.showYourCodeSection === 'true' && (
            <React.Fragment>
              <Separator middleText={i18nText.orText} />
              <CodeTitle data-testid="code-title">
                {props.codeSubtitle}
              </CodeTitle>
              <StyledCode>{props.customerReferralCode}</StyledCode>
            </React.Fragment>
          )}
      </StyledWrapper>

      <DynamicReferralEmailModal
        openModal={openModal}
        onClose={() => setOpenModal(false)}
      />
    </Container>
  )
}
